import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '../../components/ui/button';
import { Progress } from '../../components/ui/progress';
import { Spinner } from '../../components/ui/spinner';
import { setCookie } from '../../services/storage';
import { routes } from '../../routing/routes';
import { useLanguage } from '@/contexts/language-context-type';
import {
  createBusinessService,
  CreateBusinessPayload,
} from '../../services/business';
import { useAuth } from '../../hooks/use-auth';
import { useBusinessSetup } from '@/contexts/business-setup-context';
import { validationSchema } from '../../services/schema/account-setup.schema';
import { toE164, type CountryCode } from '../../lib/phone';
import { cn } from '../../lib/utils';
import { LanguageSelection } from './components/language-selection';
import {
  BusinessForm,
  type BusinessFormValues,
} from './components/business-form';
import { useOnboardingDraft } from './hooks/use-onboarding-draft';

interface AccountSetupProps {
  onComplete?: () => void;
}

const EMPTY_BUSINESS: BusinessFormValues = {
  name: '',
  type: '',
  industryType: '',
  country: '',
  logo: null,
  email: '',
  phoneNumber: '',
  website: '',
  address: '',
  city: '',
  zipCode: '',
};

/**
 * Business onboarding, in two screens.
 *
 * It was five screens and eleven required fields, none of which the customer
 * could skip and none of which they could return to after a refresh. The API
 * only ever required four things. What is asked for here is the minimum that
 * identifies a business and lets us provision an advertiser account:
 * name, logo, industry, legal entity type and country.
 *
 * The marketing profile (audience, revenue, ad spend, order value, locations)
 * and the legal details (entity name, tax ID, signed agreement, licence) moved
 * to Settings, and the pre-launch checklist demands them before a campaign can
 * go live. Nothing was dropped.
 */
export function AccountSetup({ onComplete }: Readonly<AccountSetupProps>) {
  const [step, setStep] = useState(1);

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { setLanguage, currentLanguage } = useLanguage();
  const { setHasSetupAccount, setBusiness } = useBusinessSetup();
  const { accessToken, refreshAccessToken, user } = useAuth();

  const formik = useFormik({
    initialValues: {
      language: currentLanguage || '',
      business: EMPTY_BUSINESS,
    },
    validationSchema,
    onSubmit: () => undefined,
  });

  // A refresh at the last field used to wipe everything and return the user to
  // screen one. The draft is scoped to the signed-in account and restored on
  // mount; the logo is excluded because a File cannot be serialised.
  const { clearDraft } = useOnboardingDraft({
    userId: user?.id,
    values: formik.values,
    onRestore: (restored) => {
      formik.setValues({
        language: restored.language || currentLanguage || '',
        business: { ...EMPTY_BUSINESS, ...restored.business, logo: null },
      });
      if (restored.business?.name) setStep(2);
    },
  });

  const steps = useMemo(
    () => [
      {
        id: 1,
        name: t('accountSetup.chooseLanguage'),
        description: t('accountSetup.chooseLanguageDescription'),
      },
      {
        id: 2,
        name: t('accountSetup.businessDetails'),
        description: t('accountSetup.businessDetailsDescription'),
      },
    ],
    [t]
  );

  useEffect(() => {
    if (!formik.values.language && currentLanguage) {
      formik.setFieldValue('language', currentLanguage);
    }
    // Only mirrors the resolved language into the form once it is known.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentLanguage]);

  const buildPayload = (): CreateBusinessPayload => {
    const business = formik.values.business;
    const country = business.country as CountryCode;

    const trimmed = (value: string) => {
      const next = value.trim();
      return next.length > 0 ? next : undefined;
    };

    return {
      logo: business.logo ?? undefined,
      name: business.name.trim(),
      type: business.type,
      industryType: business.industryType,
      country,
      // Left out entirely when blank: the API falls back to the address the
      // user just verified at login rather than making them retype it.
      email: trimmed(business.email),
      phoneNumber: toE164(business.phoneNumber, country),
      website: trimmed(business.website),
      address: trimmed(business.address),
      city: trimmed(business.city),
      zipCode: trimmed(business.zipCode),
      preferredLanguage: formik.values.language || undefined,
    };
  };

  const { mutate: createBusiness, isPending } = useMutation({
    mutationKey: ['create-account'],
    mutationFn: async (payload: CreateBusinessPayload) =>
      createBusinessService(payload, accessToken || undefined),
    onSuccess: async (response) => {
      if (!response?.success || !response.data) {
        throw new Error(t('accountSetup.createFailed'));
      }

      setCookie('tenantId', response.data.tenantId, 30);

      // Re-issue the token BEFORE navigating. The current one was signed before
      // this business existed, so it grants no business access and the
      // dashboard's analytics call would fail the moment we land there.
      // `silent: true` so a failed refresh leaves the current (still valid)
      // token in place instead of logging the user out — setup succeeded
      // either way, and a stale token recovers on the next refresh or re-login.
      await refreshAccessToken({ silent: true });

      // Seed the newly created business into context immediately so the
      // dashboard doesn't render with a null business on first paint.
      setBusiness(response.data);
      setHasSetupAccount(true);
      clearDraft();

      toast.success(t('accountSetup.setupComplete'));
      navigate(routes.dashboard, { replace: true });
      onComplete?.();
    },
    onError: (error) => {
      toast.error(
        error instanceof Error ? error.message : t('accountSetup.createFailed')
      );
    },
  });

  const selectLanguage = async (languageId: string) => {
    await setLanguage(languageId);
    formik.setFieldValue('language', languageId);
  };

  const handleBusinessFieldChange = (
    field: keyof BusinessFormValues,
    value: string | File | null
  ) => {
    formik.setFieldValue(`business.${field}`, value);
    formik.setFieldTouched(`business.${field}`, true, false);
  };

  const businessErrors = formik.errors.business as
    | Partial<Record<keyof BusinessFormValues, string>>
    | undefined;

  /** The five fields onboarding still insists on. */
  const canSubmit =
    !!formik.values.business.name.trim() &&
    !!formik.values.business.logo &&
    !!formik.values.business.industryType &&
    !!formik.values.business.type &&
    !!formik.values.business.country &&
    !businessErrors?.email &&
    !businessErrors?.phoneNumber &&
    !businessErrors?.website;

  const handleNext = async () => {
    if (step === 1) {
      setStep(2);
      return;
    }

    const validationErrors = await formik.validateForm();
    if (validationErrors.business) {
      formik.setTouched({
        business: Object.keys(EMPTY_BUSINESS).reduce(
          (acc, key) => ({ ...acc, [key]: true }),
          {}
        ),
      });
      toast.error(t('accountSetup.fixErrors'));
      return;
    }

    createBusiness(buildPayload());
  };

  return (
    <div className="min-h-screen py-10 bg-background-main">
      <div className="w-full max-w-3xl px-4 mx-auto space-y-8">
        <header className="space-y-4">
          <div className="space-y-1">
            <p className="text-sm font-medium text-muted-foreground">
              {t('accountSetup.stepOf', { current: step, total: steps.length })}
            </p>
            <h1 className="text-2xl font-semibold">{steps[step - 1].name}</h1>
            <p className="text-sm text-muted-foreground">
              {steps[step - 1].description}
            </p>
          </div>
          <Progress value={(step / steps.length) * 100} />
        </header>

        <main>
          {step === 1 ? (
            <LanguageSelection
              onLanguageSelect={selectLanguage}
              selectedLanguage={currentLanguage}
              languageSelected={!!formik.values.language}
            />
          ) : (
            <BusinessForm
              values={formik.values.business}
              errors={businessErrors}
              touched={
                formik.touched.business as Partial<
                  Record<keyof BusinessFormValues, boolean>
                >
              }
              accountEmail={user?.email ?? undefined}
              onFieldChange={handleBusinessFieldChange}
            />
          )}
        </main>

        <footer
          className={cn(
            'flex items-center gap-3',
            step === 1 ? 'justify-end' : 'justify-between'
          )}
        >
          {step === 2 && (
            <Button
              type="button"
              variant="outline"
              disabled={isPending}
              onClick={() => setStep(1)}
            >
              {t('common.back')}
            </Button>
          )}

          <Button
            type="button"
            onClick={handleNext}
            disabled={
              isPending || (step === 1 ? !formik.values.language : !canSubmit)
            }
          >
            {isPending ? (
              <>
                <Spinner className="mr-2 size-4" />
                {t('accountSetup.creating')}
              </>
            ) : step === 1 ? (
              t('common.continue')
            ) : (
              t('accountSetup.createBusiness')
            )}
          </Button>
        </footer>
      </div>
    </div>
  );
}
